import { ConnectedIntegration } from '@/apis/agency/businessOnboarding/types';

export type OnboardingStatus =
  | 'draft'
  | 'collecting_agent_info'
  | 'in_review'
  | 'awaiting_engineering'
  | 'business_setup'
  | 'agent_preparation'
  | 'agent_qa_testing'
  | 'agent_finalized'
  | 'lost_on_onboarding'
  | 'completed';

export const STATUS_ORDER: OnboardingStatus[] = [
  'draft',
  'collecting_agent_info',
  'in_review',
  'awaiting_engineering',
  'business_setup',
  'agent_preparation',
  'agent_qa_testing',
  'agent_finalized',
  'completed',
  'lost_on_onboarding'
];

export const POST_BUSINESS_STATUSES: OnboardingStatus[] = [
  'business_setup',
  'agent_preparation',
  'agent_qa_testing',
  'agent_finalized',
  'completed'
];

export const STATUS_CONFIG: Record<OnboardingStatus, { label: string; color: string; bg: string; chartColor: string }> =
  {
    draft: {
      label: 'Draft',
      color: '#737373',
      bg: '#F5F5F5',
      chartColor: '#D1D5DB'
    },
    collecting_agent_info: {
      label: 'Collecting Agent Info',
      color: '#2E5BFF',
      bg: '#EBF0FF',
      chartColor: '#1E3A8A'
    },
    in_review: {
      label: 'In Review',
      color: '#A855F7',
      bg: '#F3E8FF',
      chartColor: '#7C3AED'
    },
    awaiting_engineering: {
      label: 'Awaiting Engineering',
      color: '#C2410C',
      bg: '#FFEDD5',
      chartColor: '#EA580C'
    },
    business_setup: {
      label: 'Business Setup',
      color: '#3B82F6',
      bg: '#DBEAFE',
      chartColor: '#3B82F6'
    },
    agent_preparation: {
      label: 'Agent Preparation',
      color: '#374151',
      bg: '#E5E7EB',
      chartColor: '#111827'
    },
    agent_qa_testing: {
      label: 'Agent QA Testing',
      color: '#B45309',
      bg: '#FEF3C7',
      chartColor: '#92400E'
    },
    agent_finalized: {
      label: 'Agent Finalized',
      color: '#0D9488',
      bg: '#E6FFFA',
      chartColor: '#0D9488'
    },
    lost_on_onboarding: {
      label: 'Lost on Onboarding',
      color: '#EF4444',
      bg: '#FEE2E2',
      chartColor: '#EF4444'
    },
    completed: {
      label: 'Completed',
      color: '#16A34A',
      bg: '#DCFCE7',
      chartColor: '#16A34A'
    }
  };

export const AI_RECEPTIONIST_REASON_ORDER = [
  'Too many missed calls',
  'After-hour call coverage',
  'Reduce front desk workload',
  'Cost savings over hiring staff',
  'Improve patient/customer experience',
  'Lunch break call coverage',
  'Scale without adding headcount',
  'Competitor already using AI',
  'Spam call filtering',
  'Other'
] as const;

export type AIReceptionistReason = (typeof AI_RECEPTIONIST_REASON_ORDER)[number];

export const AI_RECEPTIONIST_REASON_CONFIG: Record<AIReceptionistReason, { label: string; chartColor: string }> = {
  'Too many missed calls': { label: 'Too many missed calls', chartColor: '#3B82F6' },
  'After-hour call coverage': { label: 'After hour call coverage', chartColor: '#EF4444' },
  'Reduce front desk workload': { label: 'Reduce front desk workload', chartColor: '#1E3A8A' },
  'Cost savings over hiring staff': { label: 'Cost savings over hiring staff', chartColor: '#7C3AED' },
  'Improve patient/customer experience': {
    label: 'Improve customer experience',
    chartColor: '#F97316'
  },
  'Lunch break call coverage': { label: 'Lunch break call coverage', chartColor: '#94A3B8' },
  'Scale without adding headcount': { label: 'Scale without adding headcount', chartColor: '#111827' },
  'Competitor already using AI': { label: 'Competitor already using AI', chartColor: '#0D9488' },
  'Spam call filtering': { label: 'Spam call filtering', chartColor: '#16A34A' },
  Other: { label: 'Other', chartColor: '#EAB308' }
};

export const SOURCE_CONFIG: Record<string, { label: string; color: string; bg: string; chartColor: string }> = {
  organic: {
    label: 'Organic',
    color: '#077F13',
    bg: '#DBFCDE',
    chartColor: '#1E40AF'
  },
  referral: {
    label: 'Referral',
    color: '#1E40AF',
    bg: '#EEF5FF',
    chartColor: '#93C5FD'
  }
};

export const SOURCE_FILTER_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: '0', label: 'Organic' },
  { value: '1', label: 'Referral' }
];

export const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Select' },
  ...STATUS_ORDER.map((status) => ({
    value: status,
    label: STATUS_CONFIG[status].label
  }))
];

export type BusinessStateFilter = 'all' | 'active' | 'archived';

export interface OnboardingListFormValues {
  date: string;
  status: string;
  source: string;
  business_state: BusinessStateFilter;
}

export const BUSINESS_STATE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' }
];

export interface OnboardingFilterValues {
  searchText: string | null;
  status: string;
  source: string;
  businessState: BusinessStateFilter;
}

export function buildOnboardingFilters({
  searchText,
  status,
  source,
  businessState
}: OnboardingFilterValues): Record<string, string | boolean> {
  const filters: Record<string, string | boolean> = {};

  if (searchText?.trim()) {
    filters['filter[business_legal_name_cont]'] = searchText.trim();
  }
  if (status) {
    filters['filter[status_eq]'] = status;
  }
  if (source) {
    filters['filter[source_eq]'] = source;
  }
  if (businessState === 'active') {
    filters['filter[discarded_at_null]'] = true;
  } else if (businessState === 'archived') {
    filters['filter[discarded_at_not_null]'] = true;
  }

  return filters;
}

export interface OnboardingTableRow {
  id: number;
  business_name: string;
  primary_contact: string;
  website: string;
  created_by: string;
  created_on: string;
  updated_at: string;
  status: OnboardingStatus;
  source: string;
  ticket_reference: string;
  business_plan_id?: string | number | null;
  voice_engine_id?: number | null;
  form_token?: string;
  overall_progress?: number;
  discarded_at?: string | null;
  days_in_stage: number | null;
  connected_integrations: ConnectedIntegration[];
}

export function getStageTimeColor(days: number): string {
  if (days >= 10) return 'error.main';
  if (days > 5) return 'warning.main';
  return 'primary.main';
}
